type InjectedSofiaEnvironmentConfiguration = {
  IsDevelopment: string
  IsAutomatedTest: boolean
  BffHost: string
  AppInsightsConnectionString: string
  MedgateCallEmail: string
  AcsEndpoint: string
  DisabledFeatures: string
  ReleaseCheckInterval: string
  TimeAtStartup: string
  FaroEndpoint: string
  AreEndUsersOnEnvironment: boolean
  CurrentEnvironment: string
  AllowDebugFeatures: boolean
  SpeechToTextSampleRate: string
  SpeechToTextEndpointEn: string
  SpeechToTextEndpointDe: string
  SpeechToTextEndpointFr: string
  SpeechToTextEndpointIt: string
  SpeechToTextTranscriptSaveIntervalMs: string
}

declare global {
  interface Window {
    sofia: {
      env: InjectedSofiaEnvironmentConfiguration
      nativeFetch: typeof window.fetch
      appInsightsFetch: typeof window.fetch
    }
    disableConfirmBeforeLeaving: boolean
  }
}

export const SofiaEnvironment =
  import.meta.env.MODE === "production" ? createEnvironmentWithinDocker() : createNodeDevelopmentEnvironment()

function createEnvironmentWithinDocker() {
  console.log("Injected Sofia environment", window.sofia)
  window.sofia = window.sofia || {}
  return createEnvironment(
    window.sofia.env.IsDevelopment === "true",
    window.sofia.env.IsAutomatedTest,
    window.sofia.env.BffHost,
    window.sofia.env.AppInsightsConnectionString,
    window.sofia.env.AcsEndpoint,
    window.sofia.env.DisabledFeatures,
    window.sofia.env.ReleaseCheckInterval,
    window.sofia.env.TimeAtStartup,
    window.sofia.env.FaroEndpoint,
    window.sofia.env.SpeechToTextSampleRate,
    window.sofia.env.SpeechToTextEndpointEn,
    window.sofia.env.SpeechToTextEndpointDe,
    window.sofia.env.SpeechToTextEndpointFr,
    window.sofia.env.SpeechToTextEndpointIt,
    window.sofia.env.SpeechToTextTranscriptSaveIntervalMs,
  )
}

function createNodeDevelopmentEnvironment() {
  const bffHost = import.meta.env.VITE_APP_BFF_HOST as string
  const appInsightsConnectionString = import.meta.env.VITE_APP_APPINSIGHTS_CONNECTIONSTRING as string
  const acsEndpoint = import.meta.env.VITE_APP_ACS_ENDPOINT as string
  const disabledFeatures = import.meta.env.VITE_DISABLED_FEATURES as string
  const releaseCheckInterval = import.meta.env.VITE_RELEASE_CHECK_INTERVAL as string
  const timeAtStartup = import.meta.env.VITE_TIME_AT_STARTUP as string
  const FaroEndpoint = import.meta.env.VITE_FARO_ENDPOINT as string
  const speechToTextSampleRate = import.meta.env.VITE_SPEECH_TO_TEXT_SAMPLE_RATE as string
  const speechToTextEndpointEn = import.meta.env.VITE_SPEECH_TO_TEXT_ENDPOINT_EN as string
  const speechToTextEndpointDe = import.meta.env.VITE_SPEECH_TO_TEXT_ENDPOINT_DE as string
  const speechToTextEndpointFr = import.meta.env.VITE_SPEECH_TO_TEXT_ENDPOINT_FR as string
  const speechToTextEndpointIt = import.meta.env.VITE_SPEECH_TO_TEXT_ENDPOINT_IT as string
  const speechToTextTranscriptSaveIntervalMs = import.meta.env.VITE_SPEECH_TO_TEXT_TRANSCRIPT_SAVE_INTERVAL_MS as string

  return createEnvironment(
    true,
    false,
    bffHost,
    appInsightsConnectionString,
    acsEndpoint,
    disabledFeatures,
    releaseCheckInterval,
    timeAtStartup,
    FaroEndpoint,
    speechToTextSampleRate,
    speechToTextEndpointEn,
    speechToTextEndpointDe,
    speechToTextEndpointFr,
    speechToTextEndpointIt,
    speechToTextTranscriptSaveIntervalMs,
  )
}

export type SofiaEnvironmentEntry =
  | "local"
  | "int"
  | "migr"
  | "qa"
  | "vina"
  | "vino"
  | "vinapp"
  | "uat"
  | "preprod"
  | "training"
  | "production"

// Important: always align changes here with index.html, start-nginx.sh etc
function createEnvironment(
  isDevelopment: boolean,
  isAutomatedTest: boolean,
  bffHost: string,
  appInsightsConnectionString: string,
  acsEndpoint: string,
  disabledFeatures: string,
  releaseCheckInterval: string,
  timeAtStartup: string,
  FaroEndpoint: string,
  speechToTextSampleRate: string,
  speechToTextEndpointEn: string,
  speechToTextEndpointDe: string,
  speechToTextEndpointFr: string,
  speechToTextEndpointIt: string,
  speechToTextTranscriptSaveIntervalMs: string,
) {
  type BffConfiguration = {
    IsDevelopment: boolean
    IsAutomatedTest: boolean
    Url: string
    GraphqlEndpoint: string
    Credentials: RequestCredentials
  }

  const bff: BffConfiguration = {
    IsDevelopment: isDevelopment,
    IsAutomatedTest: isAutomatedTest,
    Url: `https://${bffHost}`,
    GraphqlEndpoint: `https://${bffHost}/api/graphql/`,
    Credentials: isDevelopment ? "include" : "same-origin",
  }

  type AppInsightsConfiguration = {
    ConnectionString: string
  }

  type AcsConfigurations = {
    Endpoint: string
  }

  type SpeechToTextLanguage = "en" | "de" | "fr" | "it"

  type SpeechToTextConfiguration = {
    SampleRate: number
    Endpoints: Record<SpeechToTextLanguage, string>
    TranscriptSaveIntervalMs: number
  }

  const appInsights: AppInsightsConfiguration = {
    ConnectionString: appInsightsConnectionString,
  }

  const acs: AcsConfigurations = {
    Endpoint: acsEndpoint,
  }

  const speechToText: SpeechToTextConfiguration = {
    SampleRate: Number.parseInt(speechToTextSampleRate) || 16000,
    Endpoints: {
      en: speechToTextEndpointEn,
      de: speechToTextEndpointDe,
      fr: speechToTextEndpointFr,
      it: speechToTextEndpointIt,
    },
    TranscriptSaveIntervalMs: Number.parseInt(speechToTextTranscriptSaveIntervalMs) || 10000,
  }

  const getCurrentEnvironment = (): SofiaEnvironmentEntry => {
    if (location.hostname.includes("sofia.medgate.care")) return "production"
    if (location.hostname.includes("training")) return "training"
    if (location.hostname.includes("preprod")) return "preprod"
    if (location.hostname.includes("uat")) return "uat"
    if (location.hostname.includes("qa")) return "qa"
    if (location.hostname.includes("sofia-vinapp")) return "vinapp"
    if (location.hostname.includes("sofia-vina")) return "vina"
    if (location.hostname.includes("sofia-vino")) return "vino"
    if (location.hostname.includes("migr")) return "migr"
    if (location.hostname.includes("int")) return "int"

    return "local"
  }

  const environmentsWithDebugFeatures: SofiaEnvironmentEntry[] = ["local", "int", "qa", "vina", "vino", "vinapp", "uat"]

  const currentEnvironment = getCurrentEnvironment()

  return {
    Bff: bff,
    AppInsights: appInsights,
    Acs: acs,
    SpeechToText: speechToText,
    DisabledFeatures: (disabledFeatures ?? "").split(","),
    ReleaseCheckInterval: Number.parseInt(releaseCheckInterval) || 600000,
    TimeAtStartup: timeAtStartup,
    FaroEndpoint: FaroEndpoint,
    AreEndUsersOnEnvironment: currentEnvironment === "production" || currentEnvironment === "training",
    CurrentEnvironment: currentEnvironment,
    AllowDebugFeatures: environmentsWithDebugFeatures.includes(currentEnvironment),
    IsWindowClosing: false,
  }
}

console.log("Sofia environment", SofiaEnvironment)
